import React, { useCallback, useMemo, useState } from "react";

import { useSearchParams } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";

import { Input } from "src/base-components/Input";

type SearchInputProps = {
  placeholder: string;
  initialValue: string;
  onDebouncedChange: (value: string) => void;
  dataLoc?: string;
};

// Isolated so keystrokes only re-render the input, not the whole settings page.
export const SearchInput: React.FC<SearchInputProps> = ({
  placeholder,
  initialValue,
  onDebouncedChange,
  dataLoc,
}) => {
  const [value, setValue] = useState(initialValue);
  const notifyParent = useDebouncedCallback(onDebouncedChange, 300);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    notifyParent(e.target.value);
  };

  return (
    <Input
      data-loc={dataLoc}
      placeholder={placeholder}
      type="text"
      value={value}
      fullWidth
      onChange={handleChange}
    />
  );
};

export const useUrlSyncedSearch = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>(
    () => searchParams.get("search") || "",
  );
  const initialSearch = useMemo(
    () => searchParams.get("search") || "",
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleDebouncedSearchChange = useCallback(
    (value: string) => {
      setDebouncedSearchTerm(value);
      setSearchParams(
        (prev) => {
          const current = prev.get("search") || "";
          if (current === value) return prev;
          if (value) {
            prev.set("search", value);
          } else {
            prev.delete("search");
          }
          return prev;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return { initialSearch, debouncedSearchTerm, handleDebouncedSearchChange };
};
